import { useMemo } from "react";
import { Box, Text } from "@mantine/core";
import OrganMapDiagram, { type OrganMapMarker } from "./OrganMapDiagram";
import {
  ReportPhotoContent,
  type OrganMapPhoto,
  type ReportPhotoLayout,
} from "./ReportPhotoContent";
import {
  getReportMediaFrameHeight,
  getReportPhotoReferenceWidth,
  hasApplicableOrganMap,
} from "./reportOrganMapLayout";

export { hasApplicableOrganMap } from "./reportOrganMapLayout";

/**
 * Input data for each specimen to be displayed on organ map diagrams.
 * All data comes from the specimen's loaded relations:
 * - source.organMapTemplate (imageUrl, width, height)
 * - source (name)
 * - subsite (positionX, positionY, name)
 * - caseDiagnoses[0].category (name, markerShape, markerColor)
 */
export interface OrganMapSpecimen {
  specimenId: string;
  specimenLabel: string;
  /** Source/body-part name (e.g. Stomach, Duodenum) */
  sourceName?: string | null;
  /** The organ map template ID from the specimen's source */
  organMapTemplateId: string | null;
  /** The organ map template name */
  organMapTemplateName: string | null;
  /** S3 key of the organ map template image */
  organMapImageUrl: string | null;
  /** Template image width */
  organMapWidth: number | null;
  /** Template image height */
  organMapHeight: number | null;
  /** Subsite name */
  subsiteName: string | null;
  /** Subsite positionX (0-100 percent) on the organ map */
  positionX: number | null;
  /** Subsite positionY (0-100 percent) on the organ map */
  positionY: number | null;
  /** Diagnosis category name */
  categoryName: string | null;
  /** Diagnosis category marker shape */
  markerShape: string | null;
  /** Diagnosis category marker color */
  markerColor: string | null;
}

interface OrganMapSectionProps {
  /** Section label (e.g. "ORGAN MAP") */
  label?: string;
  /** All specimens from the case with organ map data */
  specimens: OrganMapSpecimen[];
  /** Click handler for markers */
  onMarkerClick?: (specimenId: string) => void;
  /** Compact mode */
  compact?: boolean;
  /** Fixed image height in points (pt) — width auto-scales from aspect ratio */
  imageHeight?: number;
  /** Hide all labels (section label + template names) — used for document/PDF output */
  hideLabels?: boolean;
  /** Selected report photos rendered beside applicable organ maps. */
  reportPhotos?: OrganMapPhoto[];
  /** Uses the fixed-width, three-item report composition. */
  reportMode?: boolean;
  /** Zero-based media row selected by paginated report rendering. */
  mediaRowIndex?: number;
}

/**
 * Groups specimens by their source's organ map template and renders
 * one OrganMapDiagram per unique template. Template applicability is
 * independent of marker positioning so an applicable map is never silently
 * omitted when a specimen has no subsite coordinates.
 */
export function OrganMapSection({
  label,
  specimens,
  onMarkerClick,
  compact = false,
  imageHeight,
  hideLabels = false,
  reportPhotos = [],
  reportMode = false,
  mediaRowIndex,
}: OrganMapSectionProps) {
  const referenceWidth = useMemo(
    () => getReportPhotoReferenceWidth(specimens),
    [specimens]
  );
  // Group specimens by organ map template ID
  const templateGroups = new Map<
    string,
    {
      templateName: string | null;
      imageUrl: string;
      width: number | null;
      height: number | null;
      markers: OrganMapMarker[];
    }
  >();

  specimens.filter(hasApplicableOrganMap).forEach((spec) => {
    const templateId = spec.organMapTemplateId!;
    let group = templateGroups.get(templateId);
    if (!group) {
      group = {
        templateName: spec.organMapTemplateName,
        imageUrl: spec.organMapImageUrl!,
        width: spec.organMapWidth,
        height: spec.organMapHeight,
        markers: [],
      };
      templateGroups.set(templateId, group);
    }

    group.markers.push({
      specimenId: spec.specimenId,
      specimenLabel: spec.specimenLabel,
      sourceName: spec.sourceName,
      subsiteName: spec.subsiteName,
      positionX: spec.positionX,
      positionY: spec.positionY,
      categoryName: spec.categoryName,
      markerShape: spec.markerShape,
      markerColor: spec.markerColor,
    });
  });

  const templateEntries = Array.from(templateGroups.entries());
  const rowStart = mediaRowIndex === undefined ? 0 : mediaRowIndex * 3;
  const rowEnd = mediaRowIndex === undefined ? undefined : rowStart + 3;
  const visibleTemplateEntries = templateEntries.slice(rowStart, rowEnd);
  const photoStart = Math.max(0, rowStart - templateEntries.length);
  const photoSlots = Math.max(0, (rowEnd || Infinity) - templateEntries.length);
  const visibleReportPhotos = reportPhotos.slice(
    photoStart,
    rowEnd === undefined ? undefined : photoStart + photoSlots
  );
  const visibleMediaCount =
    visibleTemplateEntries.length + visibleReportPhotos.length;
  const mediaFrameHeight =
    reportMode && visibleMediaCount > 1
      ? getReportMediaFrameHeight(
          referenceWidth,
          visibleTemplateEntries.map(([, group]) => group)
        )
      : undefined;
  const reportPhotoLayout = useMemo<ReportPhotoLayout>(() => {
    return {
      frameHeight: mediaFrameHeight,
      height: mediaFrameHeight,
      maxWidth: "100%",
      objectPosition: mediaFrameHeight ? "center bottom" : undefined,
      width: referenceWidth,
    };
  }, [mediaFrameHeight, referenceWidth]);

  if (visibleTemplateEntries.length === 0 && visibleReportPhotos.length === 0)
    return null;

  return (
    <Box>
      {label && !compact && !hideLabels && (
        <Text
          fw={700}
          size="sm"
          mb={8}
          style={{ letterSpacing: 1, borderBottom: "1px solid #333" }}
        >
          {label}
        </Text>
      )}
      <Box
        data-report-media-grid
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          justifyContent: "center",
          alignItems: "flex-start",
        }}
      >
        {visibleTemplateEntries.map(([templateId, group], index) => {
          const isTwo = visibleTemplateEntries.length === 2;
          const itemJustify =
            isTwo && !reportMode
              ? index === 0
                ? "flex-end"
                : "flex-start"
              : "center";
          return (
            <Box
              key={templateId}
              data-report-media-item="organ-map"
              style={{
                flex: reportMode
                  ? `0 0 ${referenceWidth}`
                  : isTwo
                  ? 1
                  : "0 0 auto",
                display: "flex",
                justifyContent: itemJustify,
                alignItems: "flex-start",
              }}
            >
              <OrganMapDiagram
                imageUrl={group.imageUrl}
                width={group.width}
                height={group.height}
                label={
                  !hideLabels && visibleTemplateEntries.length > 1
                    ? group.templateName || undefined
                    : undefined
                }
                markers={group.markers}
                onMarkerClick={onMarkerClick}
                compact={compact}
                imageHeight={reportMode ? undefined : imageHeight}
                imageWidth={reportMode ? referenceWidth : undefined}
                imageFrameHeight={mediaFrameHeight}
                unpositionedSpecimenDisplay={reportMode ? "hidden" : "visible"}
              />
            </Box>
          );
        })}
        {visibleReportPhotos.map((photo) => (
          <Box
            key={photo.image.id}
            data-report-media-item="photo"
            style={{ flex: `0 0 ${referenceWidth}`, maxWidth: "100%" }}
          >
            <ReportPhotoContent {...photo} layout={reportPhotoLayout} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}
